import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { Animation } from '@repo/database-core';

import CharacterEngine from '../lib/character-engine';

/**
 * Props for the Character component.
 *
 * @property {string} [className] - Optional CSS class name for styling the container
 * @property {number} [currentSkin] - Current skin index/ID for the character (default: 0)
 * @property {number} [currentHairColor] - Current hair color index/ID (default: 0)
 * @property {number} [currentHairPinch] - Current hair pinch value for styling (default: 0)
 * @property {Animation[]} baseAnimations - Base animations that define the character's core appearance
 * @property {Animation[]} [animationsToUpload] - Optional animations to be uploaded to the engine
 * @property {Animation[]} animationsToHide - Animations that should be hidden/disabled
 * @property {Animation[]} [equippedAnimations] - Optional animations for equipped items/wearables
 * @property {number} [width] - Width of the canvas in pixels (default: 1200)
 * @property {number} [height] - Height of the canvas in pixels (default: 900)
 * @property {string | null} [name] - Optional character name to display below the canvas
 */
interface CharacterProps {
  className?: string;
  currentSkin?: number;
  currentHairColor?: number;
  currentHairPinch?: number;
  baseAnimations: Animation[];
  animationsToUpload?: Animation[];
  animationsToHide: Animation[];
  equippedAnimations?: Animation[];
  width?: number;
  height?: number;
  name?: string | null;
}

/**
 * Character component for rendering animated character avatars.
 *
 * This component uses a canvas-based rendering engine to display and animate
 * character avatars with customizable appearances and equipped items. It handles
 * initialization of the rendering engine, setting up animations, and updating
 * the character's appearance based on prop changes.
 *
 * The component manages the lifecycle of the CharacterEngine, ensuring proper
 * initialization and cleanup.
 *
 * @param {CharacterProps} props - Props for configuring the character appearance and animations
 * @returns {JSX.Element} A div containing the canvas element for the character and optional name display
 */
export function Character({
  className = '',
  baseAnimations,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  animationsToUpload = [],
  animationsToHide = [],
  equippedAnimations = [],
  currentSkin = 0,
  currentHairColor = 0,
  currentHairPinch = 0,
  width = 1200,
  height = 900,
  name = null,
}: CharacterProps) {
  // Refs for DOM elements and the character engine
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<CharacterEngine | null>(null);

  /**
   * Sets the canvas dimensions based on the width and height props.
   * This ensures the canvas has the correct pixel dimensions for rendering.
   */
  const setCanvasDimensions = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = width;
      canvas.height = height;
    }
  }, [width, height]);

  // Set canvas dimensions on initial render and when dimensions change
  useLayoutEffect(() => {
    setCanvasDimensions();
  }, [setCanvasDimensions]);

  /**
   * Initializes and starts the character engine with the provided animations.
   * This function is called after the engine instance is created.
   */
  const startEngine = useCallback(async () => {
    const engine = engineRef.current;
    if (engine) {
      // Initialize the engine
      await engine.init();
      // Set the base animations that define the character
      await engine.setBaseAnimations(baseAnimations);
      // Apply any equipped animations (wearables, accessories, etc.)
      engine.setEquippedAnimations(equippedAnimations);
      // Start the rendering loop
      engine.run();
    }
  }, [baseAnimations, equippedAnimations]);

  /**
   * Sets up the character engine by creating a new instance and initializing it.
   * This is called once during component mount.
   */
  const setUpEngine = useCallback(async () => {
    const canvas = canvasRef.current;
    try {
      if (!canvas) return;

      // Create a new engine instance with the canvas
      const engineInstance = new CharacterEngine({ canvas });
      engineRef.current = engineInstance;
      // Initialize and start the engine
      startEngine();
    } catch (error) {
      console.error('Error during engine setup:', error);
    }
  }, [canvasRef, startEngine]);

  // Initialize the engine on component mount and clean up on unmount
  useEffect(() => {
    if (!engineRef.current) {
      setUpEngine();
    }

    // Cleanup function to reset and remove the engine when component unmounts
    return () => {
      engineRef.current?.reset();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update character appearance when relevant props change
  useEffect(() => {
    const engine = engineRef.current;
    // Update which animations should be hidden
    engine?.setAnimationsToHide(animationsToHide);
    // Update character skin
    engine?.updateSkin(currentSkin);
    // Update hair color
    engine?.updateHairColor(currentHairColor);
    // Update hair pinch (styling)
    engine?.updateHairPinch(currentHairPinch);
    // Update equipped items/wearables
    engine?.setEquippedAnimations(equippedAnimations);
  }, [
    animationsToHide,
    currentHairColor,
    currentHairPinch,
    currentSkin,
    equippedAnimations,
  ]);

  return (
    <div
      ref={canvasWrapRef}
      className={className}
      data-testid='canvas-wrap'
      style={{
        width,
        height,
      }}
    >
      <canvas
        data-testid='canvas'
        ref={canvasRef}
      />
      {/* Render the character name if provided */}
      {name ? (
        <p
          className={`-mt-5 w-full text-center text-2xl text-white md:-mt-10 md:text-[32px]`}
        >
          {name}
        </p>
      ) : null}
    </div>
  );
}

export default Character;
